import { type SkillFile, useDeleteSkillFile, useSkillFile } from "@/api/skills";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SkillDescriptionEditor } from "@/panes/skill/SkillDescriptionEditor";
import { SkillMarkdownEditor } from "@/panes/skill/SkillMarkdownEditor";
import { cn } from "@/utils";
import { type Skill } from "@cohesive/api-client";
import { Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

const OS_JUNK_RE =
  /(?:^|\/)(?:__MACOSX\/|\.DS_Store$|Thumbs\.db$|desktop\.ini$|\._)/;

/**
 * The skill's content surface: the description pinned at the top (the skill's
 * identity), then a file navigation (SKILL.md + any other files) over a single
 * full-width, box-free editor. SKILL.md edits as rich markdown; other markdown
 * files edit verbatim; non-markdown files are read-only previews.
 */
export function SkillContentEditor({
  skill,
  files: rawFiles,
  filesLoading = false,
}: {
  skill: Skill;
  files: SkillFile[];
  /** True while the file list is still loading — keeps the empty state from flashing. */
  filesLoading?: boolean;
}) {
  // SKILL.md leads; the rest sort by path so subfolder files (e.g.
  // references/foo.md) cluster together in the nav.
  const files = useMemo(
    () =>
      rawFiles
        .filter((f) => !OS_JUNK_RE.test(f.file_path))
        .sort((a, b) => {
          if (a.file_path === "SKILL.md") return -1;
          if (b.file_path === "SKILL.md") return 1;
          return a.file_path.localeCompare(b.file_path);
        }),
    [rawFiles],
  );

  // The description is pinned at the top of the pane, so the file nav starts on
  // SKILL.md (the canonical file, always present and sorted first).
  const [selected, setSelected] = useState<string>("SKILL.md");
  const [fileDeleteOpen, setFileDeleteOpen] = useState(false);

  const selectedInList = files.some((f) => f.file_path === selected);

  // ---- File content (for the selected file) ----
  const { data: selectedFileMeta, isError: isFileMetaError } = useSkillFile(
    skill.id,
    selectedInList ? selected : undefined,
  );

  const [fileContent, setFileContent] = useState("");
  // Which file the loaded content belongs to — gates rendering so switching files
  // never flashes the previous file's text before the new one finishes loading.
  const [loadedFile, setLoadedFile] = useState<string | null>(null);

  const { mutate: deleteSkillFile, isPending: isDeletingSkillFile } =
    useDeleteSkillFile({
      onSuccess: () => {
        toast.success("File deleted");
        setFileDeleteOpen(false);
        setSelected("SKILL.md");
      },
      onError: (e) => toast.error(e.message || "Failed to delete file"),
    });

  // Keep selection valid as files load/change: fall back to the first file
  // (SKILL.md) if the selected one isn't in the list (e.g. after a delete).
  useEffect(() => {
    if (files.length > 0 && !selectedInList) {
      setSelected(files[0].file_path);
    }
  }, [files, selectedInList]);

  useEffect(() => {
    if (!selectedInList || !selectedFileMeta?.url) {
      setLoadedFile(null);
      return;
    }
    const controller = new AbortController();
    fetch(selectedFileMeta.url, { signal: controller.signal })
      .then((res) => res.text())
      .then((text) => {
        setFileContent(text);
        setLoadedFile(selected);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === "AbortError") return;
        setFileContent("Failed to load file content.");
        setLoadedFile(selected);
      });
    return () => controller.abort();
  }, [selectedFileMeta?.url, selectedInList, selected]);

  // Content is only safe to show once it has loaded for the *current* selection.
  const contentReady = selectedInList && loadedFile === selected;

  const canDeleteSelected = selectedInList && selected !== "SKILL.md";

  return (
    <div className="flex h-full flex-col">
      {/* The description leads the pane — the skill's identity, pinned above the
          files under its own header. */}
      <div className="shrink-0">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          Description
        </p>
        {/* Keyed by updated_at so a reopened pane re-seeds when the cached
            skill was stale at mount (the editor deliberately seeds once and
            the background refetch would otherwise never reach it). */}
        <SkillDescriptionEditor
          key={`${skill.id}:${skill.updated_at}`}
          skill={skill}
        />
      </div>

      {/* Divider between the description and the file/editing area. */}
      <div className="my-4 shrink-0 border-t border-border/60" />

      {/* File picker — only rendered when the skill has more than one file
          (a lone SKILL.md needs no picker). Sits tight above the editor. */}
      <SkillFileNav
        className="mb-3"
        files={files}
        selected={selected}
        onSelect={setSelected}
        trailing={
          canDeleteSelected ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="shrink-0 text-muted-foreground hover:text-destructive"
              disabled={isDeletingSkillFile}
              onClick={() => setFileDeleteOpen(true)}
              aria-label="Delete file"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          ) : undefined
        }
      />

      {/* Full-width, box-free content for the selected file. */}
      <div className="flex min-h-0 flex-1 flex-col">
        {files.length === 0 ? (
          filesLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              This skill has no files.
            </p>
          )
        ) : isFileMetaError ? (
          <p className="text-sm text-destructive">Could not load this file.</p>
        ) : !contentReady ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : selected.endsWith(".md") ? (
          // Any markdown file is editable. Keyed per file so switching remounts
          // the editor with fresh content instead of reseeding a controlled value
          // mid-life (which flashes). SKILL.md gets frontmatter regeneration.
          <SkillMarkdownEditor
            key={selected}
            skillId={skill.id}
            filePath={selected}
            initialContent={fileContent}
          />
        ) : (
          // Non-markdown files (json, html, …) are read-only.
          <pre
            key={selected}
            className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words font-mono text-sm text-foreground/90"
          >
            {fileContent || "Empty file"}
          </pre>
        )}
      </div>

      <AlertDialog open={fileDeleteOpen} onOpenChange={setFileDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this file?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently remove &quot;{selected}&quot; from this skill. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingSkillFile}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingSkillFile}
              onClick={(e) => {
                e.preventDefault();
                deleteSkillFile({ skillId: skill.id, filePath: selected });
              }}
            >
              Delete file
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** SKILL.md leads; everything else sorts by path so subfolders cluster. */
function sortFiles<T extends { file_path: string }>(files: T[]): T[] {
  return [...files].sort((a, b) => {
    if (a.file_path === "SKILL.md") return -1;
    if (b.file_path === "SKILL.md") return 1;
    return a.file_path.localeCompare(b.file_path);
  });
}

export type SkillNavFile = { file_path: string };

/**
 * File navigation for the skill + template content views — a compact dropdown
 * (SKILL.md leads) plus an optional trailing control (e.g. a delete button).
 * Renders nothing when the skill has a single file: a lone SKILL.md needs no
 * picker, so the pane stays clean.
 */
export function SkillFileNav({
  files: rawFiles,
  selected,
  onSelect,
  trailing,
  className,
}: {
  files: SkillNavFile[];
  selected: string;
  onSelect: (filePath: string) => void;
  trailing?: React.ReactNode;
  className?: string;
}) {
  const files = useMemo(() => sortFiles(rawFiles), [rawFiles]);

  if (files.length <= 1) return null;

  return (
    <div className={cn("flex shrink-0 items-center gap-2", className)}>
      <Select value={selected} onValueChange={onSelect}>
        <SelectTrigger className="h-8 w-auto min-w-0 max-w-[18rem] gap-1.5 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {files.map((file) => (
            <SelectItem
              key={file.file_path}
              value={file.file_path}
              className="text-xs"
            >
              {file.file_path}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {trailing}
    </div>
  );
}
